import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAPIVisitorPasses } from 'services/visitor-passes/api-visitor-passes.service';
import { VisitorPassVerifyResponse } from 'services/visitor-passes/visitor-passes.service';
import { useMessenger } from 'tools/view-hooks/messenger-hook';
import { decodePassId } from './decode-pass-id';
import { verifyErrorKey } from './error-copy';
import { ReceptionController, ReceptionStage } from './interfaces';

export const useReceptionController = (service = useAPIVisitorPasses()): ReceptionController => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { showErrorMessage } = useMessenger();
    const accessToken = searchParams.get('access_token') || '';

    const [stage, setStage] = useState<ReceptionStage>('scanner');
    const [verify, setVerify] = useState<VisitorPassVerifyResponse | undefined>();
    const [errorKey, setErrorKey] = useState('reception.err-invalid');
    const [confirmedAt, setConfirmedAt] = useState('');
    const [isConfirming, setIsConfirming] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);
    const [passId, setPassId] = useState<string | null>(null);
    const [lastToken, setLastToken] = useState<string | null>(null);

    useEffect(() => {
        if (!accessToken) {
            navigate('/mobile/404');
        }
    }, []);

    // fromOffline=true es el camino de reintento (botón o listener 'online'):
    // el token ya está en mano y la pantalla de offline cubre la pantalla, así
    // que no hay motivo para pasar por 'verifying' — eso desmontaría/remontaría
    // ReceptionScanner y prendería la cámara de nuevo solo para apagarla
    // milisegundos después. En su lugar usamos isRetrying, igual que
    // isConfirming para onConfirm.
    const runVerify = (token: string, fromOffline = false) => {
        if (fromOffline) {
            setIsRetrying(true);
        } else {
            setStage('verifying');
        }
        return service
            .verifyVisitorPass(accessToken, token)
            .then((res) => {
                if (res.valid) {
                    setVerify(res);
                    setPassId(decodePassId(token));
                    setStage('valid');
                } else {
                    setErrorKey(verifyErrorKey(res.error_id));
                    setStage('invalid');
                }
            })
            .catch((err: unknown) => {
                // Sin red no hay veredicto sobre el pase: el guardia necesita una
                // pantalla que se quede, con el reintento a mano. Cualquier otro
                // error conserva el toast y devuelve la cámara.
                const isNetwork =
                    typeof err === 'object' && err !== null && (err as { isNetworkError?: boolean }).isNetworkError;
                if (isNetwork) {
                    setStage('offline');
                    return;
                }
                showErrorMessage({ key: 'reception.http-error' });
                setStage('scanner');
            })
            .finally(() => {
                if (fromOffline) {
                    setIsRetrying(false);
                }
            });
    };

    const onScan = (token: string) => {
        if (stage !== 'scanner') {
            return;
        }
        setLastToken(token);
        return runVerify(token);
    };

    const onRetryVerify = () => {
        if (!lastToken) {
            setStage('scanner');
            return;
        }
        return runVerify(lastToken, true);
    };

    const onBackToScanner = () => {
        setLastToken(null);
        setStage('scanner');
    };

    // El guardia que pierde señal en un ascensor no debería tener que tocar
    // nada al salir: cuando el navegador avisa que volvió la red, reintentamos.
    useEffect(() => {
        if (stage !== 'offline' || !lastToken) {
            return;
        }
        const onOnline = () => {
            runVerify(lastToken, true);
        };
        window.addEventListener('online', onOnline);
        return () => window.removeEventListener('online', onOnline);
        // eslint-disable-next-line
    }, [stage, lastToken]);

    const onConfirm = () => {
        if (!passId) {
            showErrorMessage({ key: 'reception.http-error' });
            return;
        }
        setIsConfirming(true);
        return service
            .confirmVisitorPass(accessToken, passId, 'qr')
            .then((res) => {
                setConfirmedAt(res.confirmed_at);
                setStage('registered');
            })
            .catch((err: unknown) => {
                // confirmVisitorPass preserva error_id en el 410 (ver
                // api-visitor-passes.service.ts). Si lo trae, un pase agotado/
                // revocado/vencido no puede quedar en 'valid' con el botón
                // activo: se muestra el motivo real y se sale de esa pantalla,
                // igual que hace onScan con verifyErrorKey. Si no lo trae (error
                // de red real) se conserva el toast genérico y se puede reintentar.
                const errorId =
                    typeof err === 'object' && err !== null ? (err as { error_id?: string }).error_id : undefined;
                if (errorId) {
                    setErrorKey(verifyErrorKey(errorId));
                    setStage('invalid');
                } else {
                    showErrorMessage({ key: 'reception.http-error' });
                }
            })
            .finally(() => setIsConfirming(false));
    };

    const reset = () => {
        setVerify(undefined);
        setPassId(null);
        setConfirmedAt('');
        setLastToken(null);
        setStage('scanner');
    };

    return {
        stage,
        verify,
        errorKey,
        confirmedAt,
        isConfirming,
        isRetrying,
        onScan,
        onConfirm,
        onCancel: reset,
        onScanNext: reset,
        onPermissionError: () => setStage('permission'),
        onRetryPermission: () => setStage('scanner'),
        onRetryVerify,
        onBackToScanner,
    };
};
